/**
 * Material → SVG Texture Mappings
 *
 * Maps each garden object type + material combination to a base SVG pattern.
 * Many materials share the same base pattern (e.g., weg.holz and terrasse.holz
 * both use holz_dielen.svg).
 *
 * SVG files are imported as URLs via Webpack/CRA.
 */

import kiesSvg from '../../assets/textures/kies.svg';
import pflasterSvg from '../../assets/textures/pflaster.svg';
import holzDielenSvg from '../../assets/textures/holz_dielen.svg';
import natursteinSvg from '../../assets/textures/naturstein.svg';
import ziegelSvg from '../../assets/textures/ziegel.svg';
import betonSvg from '../../assets/textures/beton.svg';
import rasenSvg from '../../assets/textures/rasen.svg';
import wasserSvg from '../../assets/textures/wasser.svg';
import erdeSvg from '../../assets/textures/erde.svg';
import rindenmulchSvg from '../../assets/textures/rindenmulch.svg';
import gabionenSvg from '../../assets/textures/gabionen.svg';
import metallSvg from '../../assets/textures/metall.svg';
import fliesenSvg from '../../assets/textures/fliesen.svg';
import terracottaSvg from '../../assets/textures/terracotta.svg';
import kunststoffSvg from '../../assets/textures/kunststoff.svg';
import kokosfaserSvg from '../../assets/textures/kokosfaser.svg';
import geflechtSvg from '../../assets/textures/geflecht.svg';
import schraffurSvg from '../../assets/textures/schraffur.svg';
import transparentRasterSvg from '../../assets/textures/transparent_raster.svg';
import wpcSvg from '../../assets/textures/wpc.svg';

/**
 * Material-to-texture mapping for all garden object types.
 *
 * Structure: { [objectTypeId]: { [materialValue]: svgUrl } }
 *
 * Objects without material selection use `_default` as the key.
 * The special key `_default` is also used as fallback when a material
 * value is not found in the mapping.
 */
export const MATERIAL_TEXTURE_MAP = {
  // === STRUKTUREN ===
  weg: {
    kies: kiesSvg,
    pflaster: pflasterSvg,
    holz: holzDielenSvg,
    rindenmulch: rindenmulchSvg,
    naturstein: natursteinSvg,
    _default: pflasterSvg,
  },
  mauer: {
    brick: ziegelSvg,
    stone: natursteinSvg,
    concrete: betonSvg,
    gabion: gabionenSvg,
    _default: natursteinSvg,
  },
  // zaun: line object — no pattern fill (stays color-based)
  // tor: symbol object (floor-plan door, #611) — rendered via custom _render, no pattern fill
  // rasenkante: line object — no pattern fill

  // === GEBÄUDE ===
  gebaeude: {
    _default: schraffurSvg,
  },
  gewaechshaus: {
    glas: transparentRasterSvg,
    polycarbonat: transparentRasterSvg,
    acrylglas: transparentRasterSvg,
    folie: transparentRasterSvg,
    _default: transparentRasterSvg,
  },

  // === PFLANZFLÄCHEN ===
  beet: {
    _default: erdeSvg,
  },
  hochbeet: {
    holz: holzDielenSvg,
    metall: metallSvg,
    kunststoff: kunststoffSvg,
    stein: natursteinSvg,
    beton: betonSvg,
    paletten: holzDielenSvg,
    _default: holzDielenSvg,
  },
  rasen: {
    _default: rasenSvg,
  },
  blumenkasten: {
    terracotta: terracottaSvg,
    kunststoff: kunststoffSvg,
    holz: holzDielenSvg,
    metall: metallSvg,
    _default: terracottaSvg,
  },
  blumenampel: {
    kunststoff: kunststoffSvg,
    kokos: kokosfaserSvg,
    metall: metallSvg,
    rattan: geflechtSvg,
    _default: kunststoffSvg,
  },
  topf: {
    terracotta: terracottaSvg,
    kunststoff: kunststoffSvg,
    keramik: fliesenSvg,
    metall: metallSvg,
    stein: natursteinSvg,
    _default: terracottaSvg,
  },

  // === WASSER ===
  teich: {
    _default: wasserSvg,
  },

  // === SONSTIGES ===
  kompost: {
    _default: erdeSvg,
  },
  terrasse: {
    holz: holzDielenSvg,
    wpc: wpcSvg,
    pflaster: pflasterSvg,
    naturstein: natursteinSvg,
    beton: betonSvg,
    fliesen: fliesenSvg,
    kies: kiesSvg,
    _default: holzDielenSvg,
  },
};

/**
 * Line-based object types that should NOT receive pattern fills.
 * These keep their stroke-based rendering.
 */
export const LINE_ONLY_TYPES = new Set(['zaun', 'rasenkante']);

/**
 * Symbol-based object types that render via a custom _render (not a surface
 * fill). tor (#611) draws a floor-plan door symbol and fenster (#690) a glazing
 * symbol, so pattern textures and solid-color caching would clip/override them —
 * excluded from the texture system.
 */
export const SYMBOL_ONLY_TYPES = new Set(['tor', 'fenster']);

/**
 * Get the SVG URL for a given object type and material.
 *
 * @param {string} objectType - The garden object type ID (e.g., 'weg', 'terrasse')
 * @param {string|null} material - The material value (e.g., 'kies', 'holz'), or null for default
 * @returns {string|null} SVG URL or null if no texture available
 */
export function getTextureSvgUrl(objectType, material) {
  if (LINE_ONLY_TYPES.has(objectType) || SYMBOL_ONLY_TYPES.has(objectType)) {
    return null;
  }

  const typeMap = MATERIAL_TEXTURE_MAP[objectType];
  if (!typeMap) {
    return null;
  }

  if (material && typeMap[material]) {
    return typeMap[material];
  }

  return typeMap._default || null;
}

/**
 * Check if a given object type supports textures.
 *
 * @param {string} objectType - The garden object type ID
 * @returns {boolean}
 */
export function supportsTexture(objectType) {
  if (LINE_ONLY_TYPES.has(objectType) || SYMBOL_ONLY_TYPES.has(objectType)) {
    return false;
  }
  return objectType in MATERIAL_TEXTURE_MAP;
}

/**
 * Get all available materials for an object type that have textures.
 *
 * @param {string} objectType - The garden object type ID
 * @returns {string[]} Array of material keys (excluding '_default')
 */
export function getTexturedMaterials(objectType) {
  const typeMap = MATERIAL_TEXTURE_MAP[objectType];
  if (!typeMap) {
    return [];
  }
  return Object.keys(typeMap).filter((key) => key !== '_default');
}
